/**
 * Helpers de routing tenant-aware.
 * En contexte `/t/{slug}/...`, préfixe les liens internes pour conserver le tenant.
 * Sinon, retourne le path tel quel.
 */
import { isTenantHost } from "./tenantHost";

export const getTenantPathPrefix = () => {
  if (typeof window === "undefined") return "";
  const m = window.location.pathname.match(/^\/t\/([a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?)(?:\/|$)/);
  return m ? `/t/${m[1]}` : "";
};

/** Construit un href tenant-aware. Les ancres (#...) restent absolues sur la home. */
export const tenantHref = (path) => {
  const prefix = getTenantPathPrefix();
  if (!path) return prefix || "/";
  // Ancre pure
  if (path.startsWith("#")) return `${prefix || ""}/${path}`;
  // Ancre avec '/' devant
  if (path.startsWith("/#")) return `${prefix || ""}${path}`;
  // Path absolu
  if (path.startsWith("/")) return `${prefix}${path}`;
  return `${prefix}/${path}`;
};

/**
 * The two path fields EVERY checkout payload must carry (B96), in one place so a new
 * checkout page cannot forget half the contract: the server decides the return HOST
 * (allowlist, `_checkout_origin`), the client says which PATHS — where a completed
 * payment lands (`return_path`) and where an abandoned one does (`cancel_path`).
 * `tenantHref` keeps the `/t/{slug}` prefix in path mode, where the origin alone is the
 * PLATFORM origin and the server defaults would drop the tenant — the B96 bug class.
 * Spread into the payload: `...checkoutReturnFields("/merci/boutique", "/boutique")`.
 */
export const checkoutReturnFields = (successPath, cancelPath) => ({
  return_path: tenantHref(successPath),
  cancel_path: tenantHref(cancelPath),
});

/**
 * The "go home" destination of a public page — the BUG-039 three-way rule as a PURE
 * function, shared by `useTenantHomePath` (lib/TenantContext.jsx, which documents the
 * three hosting contexts) and by `siteLinks.buildNavModel` (the chrome logo, the footer
 * links and every homepage section anchor):
 *   1. `/t/{slug}/...` path mode → the prefix itself;
 *   2. a genuine tenant host → "/";
 *   3. the platform host, outside any `/t/{slug}` prefix → the demo practice's own site,
 *      "/demo" (plain "/" there is the SaaS marketing page, not a practice site), unless
 *      a tenant document positively says this is some OTHER practice, in which case "/".
 *
 * NO ANSWER ON THE PLATFORM HOST MAY DEPEND ON A DOCUMENT ARRIVING — the BUG-039 re-open,
 * 2026-09-09. Branch 3 used to be `tenant?.is_root ? "/demo" : "/"`, so every way the
 * document can be MISSING resolved to "/", i.e. to the SaaS marketing page: the one
 * destination this whole rule exists to prevent. And missing is not an edge case —
 * `buildNavModel`'s own docstring says the tenant "may be null: the public site renders
 * before it resolves", so the chrome logo, the footer links and every section anchor of
 * the demo site pointed at the platform's shop window on the first paint of every page,
 * and kept pointing there for good whenever that one GET did not come back (a 5xx, an
 * offline moment, or — the case `docs`/memory records on prod — a proxy that answers 200
 * plus index.html for an unrecognised /api path, which used to be stored verbatim as the
 * "tenant").
 *
 * So absence now resolves the way the ROUTING already does: on the platform host, outside
 * `/t/{slug}`, the site being served IS the demo practice's, and "/demo" is the platform's
 * own route for it. `is_root` is still honoured when it is actually there and says "no" —
 * that is a caller stating this is a different practice's site, and it is respected rather
 * than overruled. What changed is only that we no longer treat SILENCE as that statement.
 */
export const tenantHomePath = (tenant) => {
  const prefix = getTenantPathPrefix();
  if (prefix) return prefix;
  if (isTenantHost()) return "/";
  // `typeof` as well as truthiness: a non-object payload is not a tenant record, and
  // reading `.is_root` off a string answers `undefined` — silence wearing a document's
  // clothes. `TenantProvider` refuses to store one, and this keeps the pure function
  // total for every other caller.
  if (!tenant || typeof tenant !== "object") return "/demo";
  return tenant.is_root ? "/demo" : "/";
};
